/**
 * MyPendingQuotesPage
 *
 * Focused list of the CURRENT USER'S own pending quotations, sorted
 * oldest-first so the most overdue land at the top. Every row has the
 * shared OrderStatusButtons segmented control so a user can mark
 * Closed / Pending / Lost inline — no need to open the detail page.
 *
 * Reuses:
 *  • GET /api/quotations  (with status=pending + sort_order=asc; for
 *    admins we additionally pass salesperson_id=self.id so admins see
 *    only their own pending, matching the dashboard card count).
 *  • components/cart/OrderStatusButtons  (unchanged).
 *  • api.updateQuotationStatus  (existing endpoint).
 *
 * When a quote is marked Closed or Lost it is removed from the local
 * list immediately (it's no longer pending) — the dashboard count
 * refreshes when the user navigates back via the app-wide `navigate(-1)`
 * pattern.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatINR, formatISTDate, useAuth } from '../lib/store';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, Eye, FileText, Loader2 } from 'lucide-react';
import { OrderStatusButtons } from '../components/cart/OrderStatusButtons';
import { logger } from '../lib/logger';

// Match QuotationsPage.daysPending: epoch-math floor, IST-neutral because
// only the day count matters for a badge on a mobile card.
const daysPending = (isoStr) => {
  if (!isoStr) return 0;
  const then = new Date(isoStr);
  if (Number.isNaN(then.getTime())) return 0;
  return Math.floor((Date.now() - then.getTime()) / 86400000);
};

export default function MyPendingQuotesPage() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const isPrivileged = can('quotations.view_all');

  // For staff/creator the backend already scopes /quotations to the
  // caller's own + on-behalf-of. For admin/manager/dispatcher we pass
  // salesperson_id=self.id so admins get their OWN pending, matching
  // the dashboard card's count (both use stale-pending-summary?mine=1).
  const fetchRows = useCallback(async (quiet = false) => {
    if (!user?.id) return;
    if (!quiet) setLoading(true);
    try {
      const params = {
        page: 1,
        limit: 200,
        status: 'pending',
        sort_order: 'asc',
      };
      if (isPrivileged) params.salesperson_id = user.id;
      const res = await api.getQuotations(params);
      setRows(res.data?.quotations || []);
      // Counted the same way as the dashboard card (a comparison once,
      // re-saves out); the list itself shows every row.
      setTotal(res.data?.counted_total ?? res.data?.total ?? 0);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isPrivileged]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Local-optimistic removal: when a row is marked closed/lost by the
  // segmented control, it no longer belongs on this pending-only list.
  // Then reload so the counted header stays honest (closing one option
  // of a comparison closes the whole comparison in the count).
  const handleStatusChange = (id) => (newStatus) => {
    if (newStatus === 'pending') return; // stays on this list
    setRows(prev => prev.filter(r => r.id !== id));
    fetchRows(true);
  };

  return (
    <div data-testid="my-pending-quotes-page">
      <div className="flex items-center gap-2 mb-4 sm:mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="rounded-xl h-9 w-9 p-0"
          data-testid="my-pending-back-btn"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-[20px] font-bold text-heda-ink">My Pending Quotes</h1>
          <p className="text-xs sm:text-sm text-heda-sub" data-testid="my-pending-count-line">
            {loading ? 'Loading…' : `${total} pending — oldest first`}
            {!loading && rows.length > total && (
              <span className="text-heda-mute"> · {rows.length} rows incl. comparison options and re-saves</span>
            )}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-heda-mute">
          <Loader2 className="w-5 h-5 animate-spin mr-2 text-heda-gold" /> Loading your pending quotes…
        </div>
      ) : rows.length === 0 ? (
        <Card className="rounded-2xl bg-white shadow-none border-heda-line">
          <CardContent className="py-10 text-center">
            <FileText className="w-10 h-10 text-heda-mute mx-auto mb-3" />
            <p className="text-sm text-heda-ink font-semibold">You have no pending quotes.</p>
            <p className="text-xs text-heda-sub mt-1">Great — nothing to chase right now.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {rows.map((q) => {
            const dp = daysPending(q.created_at);
            const isOld = dp >= 30;
            const ageBadgeClass = isOld
              ? 'bg-red-50 text-red-700 border-red-200 font-semibold'
              : 'bg-heda-cream/60 text-heda-sub border-heda-line';
            return (
              <Card key={q.id} className="rounded-2xl bg-white shadow-none border-heda-line" data-testid={`my-pending-row-${q.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs sm:text-sm font-semibold text-heda-olive-dark" data-testid={`my-pending-number-${q.id}`}>
                          {q.quotation_number}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] sm:text-xs ${ageBadgeClass}`}
                          data-testid={`my-pending-days-${q.id}`}
                        >
                          <Clock className="w-3 h-3" />
                          {dp}d pending
                        </span>
                      </div>
                      <p className="text-sm sm:text-base font-semibold text-heda-ink truncate mt-1" data-testid={`my-pending-customer-${q.id}`}>
                        {q.customer?.name || q.customer_name || 'Unknown customer'}
                      </p>
                      <p className="text-[11px] sm:text-xs text-heda-mute mt-0.5">
                        Created {formatISTDate(q.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <p className="font-bold font-mono-data text-sm sm:text-base text-heda-ink" data-testid={`my-pending-value-${q.id}`}>
                        {formatINR(q.grand_total)}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-xl h-8 px-2 text-heda-olive-dark hover:bg-heda-olive-tint"
                        onClick={() => navigate(`/quotations/${q.id}`)}
                        data-testid={`my-pending-view-${q.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <OrderStatusButtons
                      recordId={q.id}
                      status={q.order_status}
                      lostReason={q.lost_reason}
                      onStatusChange={(s, r) => api.updateQuotationStatus(q.id, { status: s, lost_reason: r })}
                      onChange={handleStatusChange(q.id)}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
